// src/components/RestaurantPOS/cashier/eod/EndOfDay.jsx
import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  TextField,
  Alert,
  Snackbar,
  Fade,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  LinearProgress,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  Stack,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningAmberIcon,
  LockClock as LockClockIcon,
  PlayArrow as PlayArrowIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  CardGiftcard as ComplimentaryIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  HourglassTop as HourglassIcon,
  ErrorOutline as ErrorIcon,
  ReceiptLong as ReceiptLongIcon,
  History as HistoryIcon,
  CleaningServices as CleaningIcon,
  DoneAll as DoneAllIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

// Import components
import Header from '../../../components/RestaurantPOS/cashier/Header';
import Footer from '../../../components/RestaurantPOS/cashier/Footer';
import Preloader from '../../../theme/Preloader';
import restaurantConfig from '../../../config/Organisation_Details';
import '../../../styles/Waiters.css';
import '../../../styles/ThermalReceipt.css';
import EODReport from './EODReport';

// Lazy load sidebars
const AfricanSidebar = lazy(() => import('../../../components/RestaurantPOS/cashier/african/Sidebar'));
const ContinentalSidebar = lazy(() => import('../../../components/RestaurantPOS/cashier/continental/Sidebar'));
const LoungeSidebar = lazy(() => import('../../../components/RestaurantPOS/cashier/lounge/Sidebar'));

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const PAYMENT_METHOD_IDS = {
  CASH: 1,
  CARD: 2,
  BANK_TRANSFER: 3,
  MOBILE_MONEY: 4,
  COMPLIMENTARY: 5,
};

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Cash', icon: <MoneyIcon />, color: '#4CAF50' },
  { value: 'CARD', label: 'Card', icon: <CreditCardIcon />, color: '#1976D2' },
  { value: 'BANK_TRANSFER', label: 'Transfer', icon: <BankIcon />, color: '#9C27B0' },
  { value: 'MIXED', label: 'Mixed', icon: <PaymentIcon />, color: '#FF6F00' },
 // { value: 'COMPLIMENTARY', label: 'Complimentary', icon: <ComplimentaryIcon />, color: '#FF9800' },
];

// Order Status Colors
const ORDER_STATUS_COLORS = {
  PENDING: { bg: '#FFF3E0', color: '#E65100' },
  IN_PROGRESS: { bg: '#E3F2FD', color: '#0D47A1' },
  READY: { bg: '#E8F5E9', color: '#1B5E20' },
  COMPLETED: { bg: '#F3E5F5', color: '#4A148C' },
  CANCELLED: { bg: '#FFEBEE', color: '#B71C1C' },
};

// Add COMPLIMENTARY to payment status colors
const PAYMENT_STATUS_COLORS = {
  UNPAID: { bg: '#FFEBEE', color: '#C62828' },
  PAID: { bg: '#E8F5E9', color: '#2E7D32' },
  PARTIAL: { bg: '#FFF3E0', color: '#E65100' },
  REFUNDED: { bg: '#F3E5F5', color: '#4A148C' },
  COMPLIMENTARY: { bg: '#F3E5F5', color: '#7B1FA2' },  // ✅ ADD THIS
  CANCELLED: { bg: '#FFEBEE', color: '#C62828' },      // ✅ ADD THIS
};

// Location configurations
const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: '#2E7D32',
    accentColor: '#C62828',
    lightColor: '#4CAF50',
    icon: '🌍',
    path: '/african-cashier',
    sidebar: AfricanSidebar,
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: '#1565C0',
    accentColor: '#C62828',
    lightColor: '#1976D2',
    icon: '🌐',
    path: '/continental-cashier',
    sidebar: ContinentalSidebar,
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: '#6A1B9A',
    accentColor: '#C62828',
    lightColor: '#9C27B0',
    icon: '🍸',
    path: '/lounge-cashier',
    sidebar: LoungeSidebar,
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (amount === null || amount === undefined) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getStatusColor = (status, type = 'order') => {
  if (type === 'order') {
    return ORDER_STATUS_COLORS[status] || { bg: '#F5F5F5', color: '#616161' };
  }
  return PAYMENT_STATUS_COLORS[status] || { bg: '#F5F5F5', color: '#616161' };
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[EOD] Error checking token expiry:', err);
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch (err) {
    console.error('[EOD] Error getting token expiry:', err);
    return 0;
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[EOD] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[EOD] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[EOD] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[EOD] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

// ============================================
// API HELPERS
// ============================================

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[EOD] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  let url = `${FULL_API_URL}${endpoint}`;
  
  if (options.params) {
    const params = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  console.debug(`[EOD] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { 
      ...options, 
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined
    });

    if (response.status === 401) {
      console.debug('[EOD] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[EOD] API Error:', err.message);
    throw err;
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const EndOfDay = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get location config
  const locationConfig = LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african;
  const LOCATION_COLOR = locationConfig.color;
  const LOCATION_NAME = locationConfig.name;
  const LOCATION_ID = locationConfig.id;
  const SidebarComponent = locationConfig.sidebar;

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [activeMenu, setActiveMenu] = useState('eod');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [displayText, setDisplayText] = useState('');

  // State for Complete & Pay dialog
const [completeAndPayDialogOpen, setCompleteAndPayDialogOpen] = useState(false);
const [selectedPendingOrder, setSelectedPendingOrder] = useState(null);
const [isCompletingAndPaying, setIsCompletingAndPaying] = useState(false);

  // EOD Report states
  const [eodReportOpen, setEodReportOpen] = useState(false);
  const [eodReportData, setEodReportData] = useState(null);

  // Data states
  const [status, setStatus] = useState(null);
  const [canStartNewDayData, setCanStartNewDayData] = useState(null);
  const [unclosedShifts, setUnclosedShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedShiftDetails, setSelectedShiftDetails] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Shift management states
  const [startingNewDay, setStartingNewDay] = useState(false);
  const [closingDay, setClosingDay] = useState(false);
  const [confirmCloseText, setConfirmCloseText] = useState('');
  const [closureReason, setClosureReason] = useState('');
  const [isHandlingSkipDays, setIsHandlingSkipDays] = useState(false);
  const [actionRequired, setActionRequired] = useState(null);
  
  // Unclosed Shift Dialog states
  const [unclosedShiftDialogOpen, setUnclosedShiftDialogOpen] = useState(false);
  const [selectedUnclosedShift, setSelectedUnclosedShift] = useState(null);
  const [isClosingUnclosedShift, setIsClosingUnclosedShift] = useState(false);
  
  // Payment Dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedUnpaidOrder, setSelectedUnpaidOrder] = useState(null);
 
  // Replace the existing payment states
const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('CASH');
const [paymentAmount, setPaymentAmount] = useState('');
const [cashAmount, setCashAmount] = useState('');
const [transferAmount, setTransferAmount] = useState('');
const [cardAmount, setCardAmount] = useState('');
const [selectedMixedMethod, setSelectedMixedMethod] = useState('CASH');
const [isMixedPayment, setIsMixedPayment] = useState(false);

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // Receipt Dialog
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [isReceiptPrinting, setIsReceiptPrinting] = useState(false);
  
  // Stats Dialog
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ============================================
  // REFS
  // ============================================
  
  const typingTimeoutRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const printWindowRef = useRef(null);

  // ============================================
  // CONFIG
  // ============================================
  
  const fullText = `${LOCATION_NAME} EOD`;
  const cashierId = parseInt(localStorage.getItem('user_id')) || null;

  // ============================================
  // COMPUTED VALUES
  // ============================================
  
  const isDayClosed = status?.is_day_closed || false;
  const canClose = status?.can_close_day || false;
  const needsSkipHandling = status?.needs_skip_handling || false;

  // ============================================
  // TYPING EFFECT
  // ============================================
  
  useEffect(() => {
    if (isLoading) return;
    
    let index = 0;
    let isDeleting = false;
    
    const typeText = () => {
      if (!isMountedRef.current) return;
      
      if (!isDeleting) {
        if (index <= fullText.length) {
          setDisplayText(fullText.substring(0, index));
          index++;
          typingTimeoutRef.current = setTimeout(typeText, 120);
        } else {
          typingTimeoutRef.current = setTimeout(() => {
            isDeleting = true;
            index = fullText.length;
            typingTimeoutRef.current = setTimeout(typeText, 50);
          }, 3000);
        }
      } else {
        if (index >= 0) {
          setDisplayText(fullText.substring(0, index));
          index--;
          typingTimeoutRef.current = setTimeout(typeText, 50);
        } else {
          isDeleting = false;
          index = 0;
          typingTimeoutRef.current = setTimeout(typeText, 500);
        }
      }
    };
    
    typingTimeoutRef.current = setTimeout(typeText, 500);
    
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [isLoading, fullText]);

  // Add this useEffect after your state declarations (around line 200-210)

useEffect(() => {
  // When unclosedShifts updates, update selectedShiftDetails if we have a selected shift
  if (selectedShift && unclosedShifts.length > 0) {
    const updatedShift = unclosedShifts.find(
      shift => shift.shift_date === selectedShift.shift_date
    );
    if (updatedShift) {
      setSelectedShiftDetails(updatedShift);
    }
  }
}, [unclosedShifts, selectedShift]);

  // ============================================
  // DATA FETCHING
  // ============================================
  
  const fetchStatus = useCallback(async () => {
    try {
      console.debug(`[EOD] Fetching day closure status for ${LOCATION_NAME}...`);
      setIsFetching(true);
      
      const data = await apiRequest('/eod/status', {
        params: { location_id: LOCATION_ID }
      });
      
      console.debug('[EOD] Status fetched:', data);
      
      if (isMountedRef.current) {
        setStatus(data);
        calculateStats(data);
      }
      
      return data;
    } catch (err) {
      console.error('[EOD] Error fetching status:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch status: ${err.message}`,
          severity: 'error',
        });
      }
      return null;
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [LOCATION_ID, LOCATION_NAME]);

 const fetchCanStartNewDay = useCallback(async () => {
  try {
    console.debug('[EOD] Checking if can start new day...');
    const data = await apiRequest('/eod/can-start-new-day', {
      params: { location_id: LOCATION_ID }
    });
    console.debug('[EOD] Can start new day data:', JSON.stringify(data, null, 2));
    
    if (isMountedRef.current) {
      setCanStartNewDayData(data);
    }
    return data;
  } catch (err) {
    console.error('[EOD] Error checking can start new day:', err);
    return null;
  }
}, [LOCATION_ID]);

  const fetchUnclosedShifts = useCallback(async () => {
    try {
      console.debug('[EOD] Fetching unclosed shifts...');
      const data = await apiRequest('/eod/unclosed-shifts', {
        params: { location_id: LOCATION_ID }
      });
      console.debug('[EOD] Unclosed shifts fetched:', data);
      
      if (isMountedRef.current) {
        setUnclosedShifts(data || []);
        if (data && data.length > 0 && !selectedShift) {
          setSelectedShift(data[0]);
          setSelectedShiftDetails(data[0]);
        }
      }
      return data;
    } catch (err) {
      console.error('[EOD] Error fetching unclosed shifts:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch unclosed shifts: ${err.message}`,
          severity: 'error',
        });
      }
      return [];
    }
  }, [LOCATION_ID, selectedShift]);

  const calculateStats = useCallback((statusData) => {
    if (!statusData) return;
    
    const totalOrders = statusData.today_orders || 0;
    const totalRevenue = statusData.today_revenue || 0;
    const pendingOrders = statusData.pending_orders_count || 0;
    const unpaidOrders = statusData.unpaid_orders_count || 0;
    const activeOrders = statusData.active_orders_count || 0;
    const completedOrders = totalOrders - activeOrders - pendingOrders;
    
    setStats({
      total_orders: totalOrders,
      total_revenue: totalRevenue,
      unpaid_orders: unpaidOrders,
      pending_orders: pendingOrders,
      active_orders: activeOrders,
      completed_orders: completedOrders,
      today_orders: totalOrders,
      today_revenue: totalRevenue,
      can_close: statusData.can_close_day || false,
      is_day_closed: statusData.is_day_closed || false,
      current_shift: statusData.current_shift || null,
    });
  }, []);

  const loadData = useCallback(async () => {
    await Promise.all([
      fetchStatus(),
      fetchCanStartNewDay(),
      fetchUnclosedShifts()
    ]);
  }, [fetchStatus, fetchCanStartNewDay, fetchUnclosedShifts]);

  // ============================================
  // INITIALIZATION
  // ============================================
  
  useEffect(() => {
    isMountedRef.current = true;
    
    console.debug(`[EOD] Initializing for ${LOCATION_NAME}...`);
    
    loadData();
    
    refreshIntervalRef.current = setInterval(() => {
      if (isMountedRef.current && !isFetching) {
        console.debug('[EOD] Auto-refreshing...');
        loadData();
      }
    }, 30000);
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }, 1000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(refreshIntervalRef.current);
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // TOKEN AUTO-REFRESH
  // ============================================
  
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();
      
      if (timeLeft > 0 && timeLeft < 120000) {
        console.debug('[EOD] Auto-refreshing token...');
        await refreshAuthToken();
      }
    };
    
    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  // ============================================
  // ACTIONS
  // ============================================
const handleCompleteAndPay = useCallback(async () => {
  if (!selectedPendingOrder) return;

  setIsCompletingAndPaying(true);
  try {
    console.debug('[EOD] Completing and paying order:', selectedPendingOrder.order_number);
    console.debug('[EOD] Order details:', selectedPendingOrder);
    
    // ✅ Check if order is already PAID
    if (selectedPendingOrder.payment_status === 'PAID') {
      setSnackbar({
        open: true,
        message: `Order ${selectedPendingOrder.order_number} is already PAID.`,
        severity: 'info',
      });
      setCompleteAndPayDialogOpen(false);
      setSelectedPendingOrder(null);
      setIsCompletingAndPaying(false);
      return;
    }
    
    // ✅ Check if order is COMPLIMENTARY
    if (selectedPendingOrder.payment_status === 'COMPLIMENTARY') {
      setSnackbar({
        open: true,
        message: `Order ${selectedPendingOrder.order_number} is COMPLIMENTARY. No payment needed.`,
        severity: 'info',
      });
      setCompleteAndPayDialogOpen(false);
      setSelectedPendingOrder(null);
      setIsCompletingAndPaying(false);
      return;
    }
    
    let totalAmount = 0;
    let paymentMethodId = PAYMENT_METHOD_IDS.CASH;
    
    // ✅ FIX: Check if order is PARTIAL
    const isPartial = selectedPendingOrder.payment_status === 'PARTIAL';
    const isComplimentary = selectedPaymentMethod === 'COMPLIMENTARY';
    
    if (isComplimentary) {
      totalAmount = 0;
      paymentMethodId = PAYMENT_METHOD_IDS.COMPLIMENTARY;
    } else {
      const methodId = PAYMENT_METHOD_IDS[selectedPaymentMethod] || PAYMENT_METHOD_IDS.CASH;
      paymentMethodId = methodId;
      
      // ✅ FIX: For PARTIAL orders, use the remaining balance
      if (isPartial) {
        const remaining = (selectedPendingOrder.total_amount || 0) - (selectedPendingOrder.total_paid || 0);
        totalAmount = Math.max(0, remaining);
        console.debug(`[EOD] PARTIAL order - Remaining balance: ${totalAmount}`);
      } else {
        totalAmount = parseFloat(selectedPendingOrder.total_amount) || 0;
      }
    }

    // Get the shift date from the order or calculate it
    const shiftDate = selectedPendingOrder.shift_date || new Date().toISOString().split('T')[0];

    const requestData = {
      order_id: selectedPendingOrder.id,
      payment_method_id: paymentMethodId,
      amount: totalAmount,
      cashier_id: cashierId,
      cashier_name: localStorage.getItem('user_name') || 'Cashier',
      shift_id: selectedPendingOrder.shift_id || null,
      shift_date: shiftDate,
    };
    
    console.debug('[EOD] Request data:', requestData);

    const response = await apiRequest('/eod/complete-and-pay-order', {
      method: 'POST',
      body: requestData
    });

    console.debug('[EOD] Complete and pay response:', response);

    if (response.success) {
      let successMessage = response.message || 'Order completed and paid successfully!';
      
      // ✅ FIX: Add table freed message if applicable
      if (response.table_freed) {
        successMessage += ' ✅ Table freed!';
      } else if (selectedPendingOrder.table_id && response.table_freed === false) {
        successMessage += ' ⚠️ Table may still be occupied. Please check.';
      }
      
      if (isPartial) {
        successMessage = `Order ${selectedPendingOrder.order_number} completed! Remaining balance of ${formatCurrency(totalAmount)} paid.`;
        if (response.table_freed) {
          successMessage += ' ✅ Table freed!';
        }
      }
      
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
      });
      setCompleteAndPayDialogOpen(false);
      setSelectedPendingOrder(null);
      setSelectedPaymentMethod('CASH');
      await loadData();
      // ✅ Refresh unclosed shifts after completion
      await fetchUnclosedShifts();
    } else {
      setSnackbar({
        open: true,
        message: response.message || 'Failed to complete and pay order',
        severity: 'error',
      });
    }
  } catch (err) {
    console.error('[EOD] Error completing and paying:', err);
    setSnackbar({
      open: true,
      message: `Failed to complete and pay: ${err.message}`,
      severity: 'error',
    });
  } finally {
    setIsCompletingAndPaying(false);
  }
}, [selectedPendingOrder, selectedPaymentMethod, cashierId, loadData, fetchUnclosedShifts]);

  const handleRefresh = useCallback(async () => {
    console.debug('[EOD] Manual refresh triggered');
    await loadData();
    setSnackbar({
      open: true,
      message: 'Data refreshed successfully!',
      severity: 'success',
    });
  }, [loadData]);

  const handleViewReport = useCallback(async () => {
    if (!status?.is_day_closed) {
      setSnackbar({
        open: true,
        message: 'Day is not closed yet. Please close the day first.',
        severity: 'warning',
      });
      return;
    }

    try {
      console.debug('[EOD] Fetching EOD report data...');
      setIsFetching(true);
      
      const data = await apiRequest('/eod/report', {
        params: { location_id: LOCATION_ID }
      });
      
      console.debug('[EOD] Report data fetched:', data);
      setEodReportData(data);
      setEodReportOpen(true);
    } catch (err) {
      console.error('[EOD] Error fetching report data:', err);
      setSnackbar({
        open: true,
        message: `Failed to fetch report: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setIsFetching(false);
    }
  }, [LOCATION_ID, status]);

  const handleEODReportClose = useCallback(() => {
    setEodReportOpen(false);
  }, []);

  const handleSkippedDays = useCallback(async () => {
    setIsHandlingSkipDays(true);
    try {
      console.debug('[EOD] Handling skipped days...');
      
      const response = await apiRequest('/eod/handle-skipped-days', {
        method: 'POST',
        params: { location_id: LOCATION_ID }
      });

      console.debug('[EOD] Skipped days handled:', response);

      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Skipped days have been handled successfully!',
          severity: 'success',
        });
        await loadData();
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to handle skipped days',
          severity: 'error',
        });
      }
    } catch (err) {
      console.error('[EOD] Error handling skipped days:', err);
      setSnackbar({
        open: true,
        message: `Failed to handle skipped days: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setIsHandlingSkipDays(false);
    }
  }, [LOCATION_ID, loadData]);

 // src/components/RestaurantPOS/cashier/eod/EndOfDay.jsx

// In EndOfDay.jsx - update handleStartNewDay

const handleStartNewDay = useCallback(async () => {
  setStartingNewDay(true);
  try {
    console.debug('[EOD] Starting new day...');
    
    // First, check if we need to handle skipped days
    if (needsSkipHandling) {
      await handleSkippedDays();
      // After handling skipped days, try again
      setTimeout(() => {
        handleStartNewDay();
      }, 500);
      return;
    }
    
    // Check if we can start a new day first
    const canStart = await fetchCanStartNewDay();
    console.debug('[EOD] Can start new day data:', canStart);
    
    if (!canStart || !canStart.can_start) {
      const reasons = canStart?.reasons || ['Unknown reason'];
      const reasonMessage = reasons.join(', ');
      setSnackbar({
        open: true,
        message: `Cannot start new day: ${reasonMessage}`,
        severity: 'warning',
      });
      setStartingNewDay(false);
      return;
    }
    
    const response = await apiRequest('/eod/start-new-day', {
      method: 'POST',
      body: {
        staff_id: cashierId,
        staff_name: localStorage.getItem('user_name') || 'Cashier',
        location_id: LOCATION_ID
      },
      params: { location_id: LOCATION_ID }
    });

    console.debug('[EOD] Start new day response:', response);

    if (response.success) {
      setSnackbar({
        open: true,
        message: response.message || 'New day started successfully!',
        severity: 'success',
      });
      await loadData();
      setTimeout(() => window.location.reload(), 2000);
    } else if (response.action_required === 'close_previous') {
      // Handle pending orders
      setActionRequired({
        type: 'pending_orders',
        data: response.pending_orders,
        message: response.message
      });
      setSnackbar({
        open: true,
        message: response.message,
        severity: 'warning',
      });
    } else if (response.action_required === 'record_payments') {
      // Handle unpaid orders
      setActionRequired({
        type: 'unpaid_orders',
        data: response.unpaid_orders,
        message: response.message
      });
      setSnackbar({
        open: true,
        message: response.message,
        severity: 'warning',
      });
    } else if (response.action_required === 'day_closed') {
      // Handle day already closed
      setSnackbar({
        open: true,
        message: `❌ ${response.message}`,
        severity: 'error',
      });
      // Optionally show a dialog with more details
      setActionRequired({
        type: 'day_closed',
        data: response,
        message: response.message
      });
    } else {
      setSnackbar({
        open: true,
        message: response.message || 'Failed to start new day',
        severity: 'error',
      });
    }
  } catch (err) {
    console.error('[EOD] Error starting new day:', err);
    setSnackbar({
      open: true,
      message: `Failed to start new day: ${err.message}`,
      severity: 'error',
    });
  } finally {
    setStartingNewDay(false);
  }
}, [LOCATION_ID, cashierId, loadData, needsSkipHandling, handleSkippedDays, fetchCanStartNewDay]);

  const handleCloseUnclosedShift = useCallback(async (shiftDate) => {
    setIsClosingUnclosedShift(true);
    try {
        console.debug(`[EOD] Closing unclosed shift for date: ${shiftDate}`);
        
        // Use the apiRequest with proper parameters
        const response = await apiRequest('/eod/close-unclosed-shift', {
            method: 'POST',
            params: {
                location_id: LOCATION_ID,
                shift_date: shiftDate
            },
            body: {
                closure_reason: 'Closing unclosed shift to clean up operations',
                cashier_id: parseInt(localStorage.getItem('user_id')) || null,
                cashier_name: localStorage.getItem('user_name') || 'Cashier'
            }
        });

        console.debug('[EOD] Close unclosed shift response:', response);

        if (response.success) {
            setSnackbar({
                open: true,
                message: response.message || 'Unclosed shift closed successfully!',
                severity: 'success',
            });
            setUnclosedShiftDialogOpen(false);
            setSelectedUnclosedShift(null);
            await loadData();
        } else {
            setSnackbar({
                open: true,
                message: response.message || 'Failed to close unclosed shift',
                severity: 'error',
            });
        }
    } catch (err) {
        console.error('[EOD] Error closing unclosed shift:', err);
        setSnackbar({
            open: true,
            message: `Failed to close unclosed shift: ${err.message}`,
            severity: 'error',
        });
    } finally {
        setIsClosingUnclosedShift(false);
    }
}, [LOCATION_ID, loadData]);

  const handleCloseDay = useCallback(async () => {
    if (confirmCloseText !== 'CLOSE DAY') {
      setSnackbar({
        open: true,
        message: 'Please type "CLOSE DAY" to confirm',
        severity: 'error',
      });
      return;
    }

    setClosingDay(true);
    try {
      console.debug('[EOD] Closing day...');
      
      const response = await apiRequest('/eod/close-day', {
        method: 'POST',
        body: {
          closure_reason: closureReason || 'End of day closure',
          cashier_id: cashierId,
          cashier_name: localStorage.getItem('user_name') || 'Cashier'
        },
        params: { location_id: LOCATION_ID }
      });

      console.debug('[EOD] Close day response:', response);

      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || 'Day closed successfully!',
          severity: 'success',
        });
        setConfirmCloseText('');
        setClosureReason('');
        await loadData();
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to close day',
          severity: 'error',
        });
      }
    } catch (err) {
      console.error('[EOD] Error closing day:', err);
      setSnackbar({
        open: true,
        message: `Failed to close day: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setClosingDay(false);
    }
  }, [LOCATION_ID, cashierId, confirmCloseText, closureReason, loadData]);

 const handleRecordPayment = useCallback(async () => {
  if (!selectedUnpaidOrder) return;

  setIsProcessingPayment(true);
  try {
    console.debug('[EOD] Recording payment for order:', selectedUnpaidOrder.order_number);
    
    let totalAmount = 0;
    let paymentMethodId = PAYMENT_METHOD_IDS.CASH;
    let isComplimentary = false;
    
    if (selectedPaymentMethod === 'MIXED') {
      const cash = parseFloat(cashAmount) || 0;
      const transfer = parseFloat(transferAmount) || 0;
      const card = parseFloat(cardAmount) || 0;
      const total = cash + transfer + card;
      
      if (total <= 0) {
        setSnackbar({
          open: true,
          message: 'Please enter valid payment amounts for mixed payment',
          severity: 'warning',
        });
        setIsProcessingPayment(false);
        return;
      }
      
      totalAmount = total;
      paymentMethodId = PAYMENT_METHOD_IDS.CASH;
    } else if (selectedPaymentMethod === 'COMPLIMENTARY') {
      totalAmount = 0;
      isComplimentary = true;
      paymentMethodId = PAYMENT_METHOD_IDS.COMPLIMENTARY;
    } else {
      const methodId = PAYMENT_METHOD_IDS[selectedPaymentMethod] || PAYMENT_METHOD_IDS.CASH;
      paymentMethodId = methodId;
      totalAmount = selectedUnpaidOrder.total_amount;
    }

    // Get the shift date
    const shiftDate = selectedUnpaidOrder.shift_date || new Date().toISOString().split('T')[0];

    const response = await apiRequest('/eod/record-payment', {
      method: 'POST',
      body: {
        order_id: selectedUnpaidOrder.id,
        payment_method_id: paymentMethodId,
        amount: totalAmount,
        cashier_id: cashierId,
        cashier_name: localStorage.getItem('user_name') || 'Cashier',
        shift_id: selectedUnpaidOrder.shift_id || null,
        shift_date: shiftDate,  // ✅ ADD THIS REQUIRED FIELD
      }
    });

    console.debug('[EOD] Record payment response:', response);

    if (response.success) {
      setSnackbar({
        open: true,
        message: response.message || 'Payment recorded successfully!',
        severity: 'success',
      });
      setPaymentDialogOpen(false);
      setSelectedUnpaidOrder(null);
      setSelectedPaymentMethod('CASH');
      setPaymentAmount('');
      setCashAmount('');
      setTransferAmount('');
      setCardAmount('');
      setPaymentSuccess(true);
      await loadData();
      setTimeout(() => setPaymentSuccess(false), 3000);
    } else {
      setSnackbar({
        open: true,
        message: response.message || 'Failed to record payment',
        severity: 'error',
      });
    }
  } catch (err) {
    console.error('[EOD] Error recording payment:', err);
    setSnackbar({
      open: true,
      message: `Failed to record payment: ${err.message}`,
      severity: 'error',
    });
  } finally {
    setIsProcessingPayment(false);
  }
}, [selectedUnpaidOrder, selectedPaymentMethod, paymentAmount, cashAmount, transferAmount, cardAmount, selectedShift, cashierId, loadData]);

// ============================================
  // PRINT FUNCTIONS
  // ============================================
  
  const openPrintWindow = useCallback((htmlContent, title) => {
    if (printWindowRef.current && !printWindowRef.current.closed) {
      printWindowRef.current.close();
    }
    
    const printWindow = window.open('', '_blank', 'width=400,height=600,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes');
    
    if (!printWindow) {
      setSnackbar({
        open: true,
        message: 'Please allow popups for printing',
        severity: 'warning',
      });
      return null;
    }
    
    printWindowRef.current = printWindow;
    
    const allStyles = document.querySelectorAll('style');
    let combinedStyles = '';
    allStyles.forEach(style => {
      combinedStyles += style.innerHTML;
    });
    
    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${title}</title>
          <style>
            ${combinedStyles}
            @media print {
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { margin: 0; padding: 0; background: white; }
              .no-print { display: none !important; }
              #print-content { max-width: 80mm; margin: 0 auto; padding: 8px 6px; }
            }
            body {
              background: #f0f0f0;
              display: flex;
              flex-direction: column;
              align-items: center;
              padding: 20px;
              font-family: Arial, sans-serif;
            }
            #print-content {
              max-width: 80mm;
              margin: 0 auto;
              background: white;
              padding: 10px 8px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.1);
              border-radius: 4px;
            }
            .print-instructions {
              text-align: center;
              padding: 10px;
              margin-bottom: 10px;
              background: #e3f2fd;
              border-radius: 4px;
              font-family: Arial, sans-serif;
              font-size: 12px;
              max-width: 80mm;
            }
            .print-button {
              display: block;
              width: 100%;
              max-width: 80mm;
              padding: 12px;
              margin-top: 10px;
              background: #1976D2;
              color: white;
              border: none;
              border-radius: 4px;
              font-size: 14px;
              font-weight: bold;
              cursor: pointer;
            }
            .print-button:hover {
              background: #1565C0;
            }
          </style>
        </head>
        <body>
          <div class="print-instructions no-print">
            🖨️ Click the "Print" button below to print
            <br>
            <small>${title}</small>
          </div>
          <div id="print-content">
            ${htmlContent}
          </div>
          <button class="print-button no-print" onclick="window.print(); setTimeout(function() { window.close(); }, 3000);">
            🖨️ Print ${title}
          </button>
          <script>
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 3000);
            }, 500);
          <\/script>
        </body>
      </html>
    `;
    
    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    return printWindow;
  }, []);

  // ============================================
  // RECEIPT HANDLING
  // ============================================
  
  // In EndOfDay.jsx - Update generateThermalReceipt (around line 620)

const generateThermalReceipt = useCallback((order) => {
  if (!order) return '';
  
  const ticketTotal = order.total_amount || 0;
  const currencySymbol = '₦';
  const formatCurrencyLocal = (amount) => {
    return `${currencySymbol} ${Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };
  
  const formatDateLocal = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };
  
  const formatTimeLocal = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };
  
  const waiterName = order.waiter_name || localStorage.getItem('user_name') || 'Waiter';
  const tableName = order.table_name || 'N/A';
  const orderType = order.order_type || 'DINE_IN';
  const paymentStatus = order.payment_status || 'UNPAID';
  
  // ✅ ADD: Determine receipt type
  const isComplimentary = paymentStatus === 'COMPLIMENTARY';
  const isCancelled = paymentStatus === 'CANCELLED';
  const isRefunded = paymentStatus === 'REFUNDED';
  const isPaid = paymentStatus === 'PAID';
  
  let receiptTitle = 'PAYMENT RECEIPT';
  let receiptColor = '#000000';
  let receiptBadge = '';
  
  if (isComplimentary) {
    receiptTitle = 'COMPLIMENTARY RECEIPT';
    receiptColor = '#9C27B0';
    receiptBadge = '🌟 COMPLIMENTARY - NO CHARGE';
  } else if (isCancelled) {
    receiptTitle = 'CANCELLED ORDER RECEIPT';
    receiptColor = '#F44336';
    receiptBadge = '❌ ORDER CANCELLED';
  } else if (isRefunded) {
    receiptTitle = 'REFUNDED ORDER RECEIPT';
    receiptColor = '#9C27B0';
    receiptBadge = '🔄 ORDER REFUNDED';
  }
  
  // ✅ UPDATED: Use the determined title and color
  return `
    <div style="max-width:80mm;margin:0 auto;padding:8px 6px;background:#ffffff;font-family:'Courier New',monospace;font-size:10px;line-height:1.6;color:#000000;font-weight:bold;">
      <div style="text-align:center;padding-bottom:10px;border-bottom:3px solid #000;margin-bottom:10px;">
        <div style="font-size:18px;font-weight:bold;letter-spacing:2px;color:#000;">${restaurantConfig.name || 'RESTAURANT'}</div>
        ${restaurantConfig.address ? `<div style="font-size:8px;font-weight:bold;color:#333;">${restaurantConfig.address}</div>` : ''}
        ${restaurantConfig.phone ? `<div style="font-size:8px;font-weight:bold;color:#333;">Tel: ${restaurantConfig.phone}</div>` : ''}
        <div style="border-top:1px dashed #000;margin:6px 0;"></div>
        <div style="font-size:14px;font-weight:bold;letter-spacing:2px;color:${receiptColor};">${receiptTitle}</div>
        <div style="font-size:9px;font-weight:bold;color:#333;margin-top:3px;">${formatDateLocal(order.order_time)} ${formatTimeLocal(order.order_time)}</div>
        ${receiptBadge ? `<div style="font-size:10px;font-weight:bold;color:${receiptColor};margin-top:3px;">${receiptBadge}</div>` : ''}
      </div>
      
      <div style="margin-bottom:6px;padding:4px 2px;background:#f5f5f5;border-radius:4px;">
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
          <span>ORDER: ${order.order_number}</span>
          <span>#${String(order.serial_number || 0).padStart(6, '0')}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
          <span>Table: ${tableName}</span>
          <span>${orderType}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
          <span>Waiter: ${waiterName}</span>
          <span>${LOCATION_NAME}</span>
        </div>
        ${order.customer_name ? `
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>Customer: ${order.customer_name}</span>
            ${order.customer_phone ? `<span>${order.customer_phone}</span>` : ''}
          </div>
        ` : ''}
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
          <span>Status:</span>
          <span style="color:${isComplimentary ? '#9C27B0' : isCancelled ? '#F44336' : isRefunded ? '#9C27B0' : '#2E7D32'};">${paymentStatus}</span>
        </div>
      </div>
      
      <div style="border-top:2px solid #000;margin:6px 0;"></div>
      
      <div style="margin-bottom:6px;">
        <div style="display:flex;justify-content:space-between;padding:4px 2px;border-bottom:2px solid #000;font-weight:bold;font-size:8px;background-color:#e0e0e0;color:#000;">
          <span style="font-size:8px;font-weight:bold;flex:1;">ITEM</span>
          <span style="font-size:8px;font-weight:bold;min-width:25px;text-align:center;">QTY</span>
          <span style="font-size:8px;font-weight:bold;min-width:65px;text-align:right;">TOTAL</span>
        </div>
        
        ${(order.items || []).map((item, idx) => {
          const quantity = item.quantity || 1;
          const name = item.name || 'Item';
          const price = item.price || item.amount || 0;
          const total = price * quantity;
          
          return `
            <div style="margin-bottom:2px;" key="${idx}">
              <div style="display:flex;justify-content:space-between;padding:3px 0;font-size:9px;font-weight:bold;border-bottom:1px solid #ccc;color:#000;">
                <span style="font-size:9px;font-weight:bold;flex:1;">${name}</span>
                <span style="font-size:9px;font-weight:bold;min-width:25px;text-align:center;">${quantity}</span>
                <span style="font-size:9px;font-weight:bold;min-width:65px;text-align:right;">${formatCurrencyLocal(total)}</span>
              </div>
              ${item.category ? `
                <div style="padding-left:4px;margin-bottom:2px;font-size:7px;color:#666;font-weight:bold;">
                  ${item.category}
                </div>
              ` : ''}
            </div>
          `;
        }).join('')}
      </div>
      
      <div style="border-top:2px solid #000;margin:6px 0;"></div>
      
      <div style="padding:4px 0;">
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
          <span>SUBTOTAL</span>
          <span>${formatCurrencyLocal(order.subtotal || 0)}</span>
        </div>
        ${order.tax > 0 ? `
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>TAX</span>
            <span>${formatCurrencyLocal(order.tax)}</span>
          </div>
        ` : ''}
        ${order.discount > 0 ? `
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>DISCOUNT</span>
            <span>-${formatCurrencyLocal(order.discount)}</span>
          </div>
        ` : ''}
      </div>
      
      <div style="border-top:3px double #000;margin:4px 0;"></div>
      
      <div style="display:flex;justify-content:space-between;padding:6px 4px;font-size:14px;font-weight:bold;color:#000;background:#f5f5f5;border-radius:4px;">
        <span>TOTAL</span>
        <span>${formatCurrencyLocal(ticketTotal)}</span>
      </div>
      
      <div style="border-top:1px dashed #000;margin:6px 0;"></div>
      <div style="padding:4px 0;">
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
          <span>PAYMENT STATUS</span>
          <span style="color:${isComplimentary ? '#9C27B0' : isCancelled ? '#F44336' : isRefunded ? '#9C27B0' : '#2E7D32'};">${paymentStatus}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
          <span>TOTAL PAID</span>
          <span>${formatCurrencyLocal(order.total_paid || 0)}</span>
        </div>
        ${order.balance > 0 ? `
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#C62828;">
            <span>BALANCE</span>
            <span>${formatCurrencyLocal(order.balance)}</span>
          </div>
        ` : ''}
      </div>
      
      ${order.notes ? `
        <div style="border-top:1px dashed #000;margin:6px 0;"></div>
        <div style="margin-bottom:4px;padding:4px;background:#fff8e1;border-radius:4px;">
          <div style="font-size:7px;color:#555;text-transform:uppercase;font-weight:bold;">Notes</div>
          <div style="font-size:9px;color:#000;font-weight:bold;">${order.notes}</div>
        </div>
      ` : ''}
      
      <div style="text-align:center;padding-top:10px;border-top:3px solid #000;margin-top:10px;">
        <div style="font-size:10px;font-weight:bold;color:#000;">Thank you for your patronage!</div>
        <div style="border-top:1px dashed #000;margin:6px 0;"></div>
        <div style="font-size:6px;color:#666;margin-top:2px;font-weight:bold;">
          ${restaurantConfig.name || 'Restaurant'} • ${receiptTitle}
        </div>
        <div style="font-size:6px;color:#999;margin-top:2px;font-weight:bold;">
          ${isComplimentary ? 'Complimentary - No Charge' : isCancelled ? 'Cancelled Order' : isRefunded ? 'Refunded Order' : 'Payment recorded on'} ${new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  `;
}, [LOCATION_NAME]);

  // In EndOfDay.jsx - Update handlePrintReceiptAction (around line 570)

const handlePrintReceiptAction = useCallback(async (order) => {
  try {
    console.debug(`[EOD] Printing receipt for order: ${order.order_number}`);
    setIsReceiptPrinting(true);
    
    // ✅ UPDATED: Allow printing for all resolved statuses
    const resolvedStatuses = ['PAID', 'REFUNDED', 'COMPLIMENTARY', 'CANCELLED'];
    if (!resolvedStatuses.includes(order.payment_status)) {
      setSnackbar({
        open: true,
        message: 'This order has not been resolved yet. Please process payment first.',
        severity: 'warning',
      });
      setIsReceiptPrinting(false);
      return;
    }
      
    const receiptHTML = generateThermalReceipt(order);
    const printWindow = openPrintWindow(receiptHTML, `Receipt - ${order.order_number}`);
    
    if (printWindow) {
      setSnackbar({
        open: true,
        message: `Receipt for ${order.order_number} sent to printer!`,
        severity: 'success',
      });
    }
      
  } catch (err) {
    console.error('[EOD] Error printing receipt:', err);
    setSnackbar({
      open: true,
      message: `Failed to print receipt: ${err.message}`,
      severity: 'error',
    });
  } finally {
    setTimeout(() => {
      setIsReceiptPrinting(false);
    }, 1000);
  }
}, [generateThermalReceipt, openPrintWindow]);

  const handleCloseReceiptDialog = useCallback(() => {
    setReceiptDialogOpen(false);
    setReceiptData(null);
    setIsReceiptPrinting(false);
  }, []);

  // ============================================
  // RENDER HELPERS
  // ============================================
  
 // In EndOfDay.jsx - Update getStatusChip function (around line 370)

const getStatusChip = useCallback((status, type = 'order') => {
  const colors = getStatusColor(status, type);
  const label = status?.replace('_', ' ') || 'Unknown';
  
  let chipSx = {
    bgcolor: colors.bg,
    color: colors.color,
    fontWeight: 700,
    fontSize: { xs: '0.6rem', sm: '0.75rem' },
    height: { xs: 22, sm: 26 },
    '& .MuiChip-label': {
      px: { xs: 1, sm: 1.5 },
    },
  };
  
  // Special styling for COMPLIMENTARY
  if (status === 'COMPLIMENTARY') {
    chipSx = {
      ...chipSx,
      bgcolor: '#F3E5F5',
      color: '#7B1FA2',
      border: '1px solid #9C27B0',
    };
  }
  
  // Special styling for CANCELLED
  if (status === 'CANCELLED') {
    chipSx = {
      ...chipSx,
      bgcolor: '#FFEBEE',
      color: '#C62828',
      border: '1px solid #F44336',
    };
  }
  
  // ✅ ADD: Special styling for REFUNDED
  if (status === 'REFUNDED') {
    chipSx = {
      ...chipSx,
      bgcolor: '#F3E5F5',
      color: '#4A148C',
      border: '1px solid #9C27B0',
    };
  }
  
  // ✅ ADD: Special styling for PAID
  if (status === 'PAID') {
    chipSx = {
      ...chipSx,
      bgcolor: '#E8F5E9',
      color: '#2E7D32',
      border: '1px solid #4CAF50',
    };
  }
  
  return (
    <Chip
      label={label}
      size="small"
      sx={chipSx}
    />
  );
}, []);
  // ============================================
  // RENDER FUNCTIONS - Stats Summary
  // ============================================
  
  const renderStatsSummary = useCallback(() => {
    if (!stats) return null;
    
    const statCards = [
      {
        title: 'Today\'s Orders',
        value: stats.today_orders || 0,
        icon: <ReceiptIcon />,
        color: '#1976D2',
      },
      {
        title: 'Active Orders',
        value: stats.active_orders || 0,
        icon: <HourglassIcon />,
        color: '#E65100',
      },
      {
        title: 'Unpaid Orders',
        value: stats.unpaid_orders || 0,
        icon: <ErrorIcon />,
        color: '#C62828',
      },
      {
        title: 'Today\'s Revenue',
        value: formatCurrency(stats.today_revenue || 0),
        icon: <TrendingUpIcon />,
        color: '#2E7D32',
      },
    ];
    
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2.5 },
                borderRadius: '14px',
                border: `1px solid ${alpha(stat.color, 0.15)}`,
                bgcolor: alpha(stat.color, 0.05),
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: theme.shadows[3],
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(stat.color, 0.15),
                  color: stat.color,
                  width: { xs: 36, sm: 48 },
                  height: { xs: 36, sm: 48 },
                }}
              >
                {stat.icon}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  fontWeight={600} 
                  sx={{ 
                    fontSize: { xs: '0.55rem', sm: '0.8rem' },
                    display: 'block',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {stat.title}
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight={700} 
                  sx={{ 
                    fontSize: { xs: '0.85rem', sm: '1.2rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {stat.value}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }, [stats, theme]);

  // ============================================
  // RENDER FUNCTIONS - Status Section
  // ============================================

  const renderStatusSection = () => {
    if (!status) return null;

    const isClosed = status.is_day_closed;
    const canCloseDay = status.can_close_day;
    const hasActiveOrders = status.active_orders_count > 0;
    const hasUnpaidOrders = status.unpaid_orders_count > 0;
    const hasPendingOrders = status.pending_orders_count > 0;

    let statusIcon = <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: { xs: 30, sm: 40 } }} />;
    let statusText = '';

    if (isClosed) {
      statusIcon = <LockClockIcon sx={{ color: '#D32F2F', fontSize: { xs: 30, sm: 40 } }} />;
      statusText = `🔒 ${LOCATION_NAME} - Day Closed`;
    } else if (needsSkipHandling) {
      statusIcon = <ScheduleIcon sx={{ color: '#ED6C02', fontSize: { xs: 30, sm: 40 } }} />;
      statusText = `⏳ ${LOCATION_NAME} - System Idle (Skipped Days)`;
    } else if (canCloseDay) {
      statusIcon = <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: { xs: 30, sm: 40 } }} />;
      statusText = `✅ ${LOCATION_NAME} - Day Ready to Close`;
    } else {
      statusIcon = <WarningAmberIcon sx={{ color: '#ED6C02', fontSize: { xs: 30, sm: 40 } }} />;
      statusText = `⏳ ${LOCATION_NAME} - Day in Progress`;
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 3 },
            borderRadius: '16px',
            border: `2px solid ${isClosed ? '#D32F2F' : needsSkipHandling ? '#ED6C02' : canCloseDay ? '#4CAF50' : '#ED6C02'}`,
            bgcolor: isClosed ? alpha('#D32F2F', 0.05) : needsSkipHandling ? alpha('#ED6C02', 0.05) : canCloseDay ? alpha('#4CAF50', 0.05) : alpha('#ED6C02', 0.05),
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: { xs: 1.5, sm: 2 },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
              {statusIcon}
              <Box>
                <Typography 
                  variant="h6" 
                  fontWeight="bold" 
                  sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
                >
                  {statusText}
                </Typography>
                {status.skip_message && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ display: 'block', fontSize: { xs: '0.6rem', sm: '0.8rem' } }}
                  >
                    {status.skip_message}
                  </Typography>
                )}
              </Box>
            </Box>
            
            <Box sx={{ 
              flex: 1, 
              width: { xs: '100%', sm: 'auto' },
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              <Chip
                label={`Active: ${status.active_orders_count || 0}`}
                size="small"
                color={hasActiveOrders ? 'warning' : 'success'}
                sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
              />
              <Chip
                label={`Unpaid: ${status.unpaid_orders_count || 0}`}
                size="small"
                color={hasUnpaidOrders ? 'error' : 'success'}
                sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
              />
              <Chip
                label={`Pending: ${status.pending_orders_count || 0}`}
                size="small"
                color={hasPendingOrders ? 'warning' : 'success'}
                sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
              />
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' },
              mt: { xs: 1, sm: 0 },
            }}>
              {needsSkipHandling && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={isHandlingSkipDays ? <CircularProgress size={20} /> : <CleaningIcon />}
                  onClick={handleSkippedDays}
                  disabled={isHandlingSkipDays || startingNewDay}
                  sx={{ 
                    borderRadius: 2,
                    flex: { xs: 1, sm: 'none' },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 },
                  }}
                >
                  {isHandlingSkipDays ? 'Handling...' : 'Handle Skipped Days'}
                </Button>
              )}
              {(isClosed || needsSkipHandling) ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={startingNewDay ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                  onClick={handleStartNewDay}
                  disabled={startingNewDay || isHandlingSkipDays}
                  sx={{ 
                    borderRadius: 2,
                    flex: { xs: 1, sm: 'none' },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 },
                  }}
                >
                  {startingNewDay ? 'Starting...' : 'Start New Day'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={closingDay ? <CircularProgress size={20} /> : <LockClockIcon />}
                  onClick={() => document.getElementById('close-day-section')?.scrollIntoView({ behavior: 'smooth' })}
                  disabled={!canClose || closingDay}
                  sx={{ 
                    borderRadius: 2,
                    flex: { xs: 1, sm: 'none' },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 },
                  }}
                >
                  {closingDay ? 'Closing...' : 'Close Day'}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={isFetching}
                sx={{ 
                  borderRadius: 2,
                  flex: { xs: 1, sm: 'none' },
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  py: { xs: 0.75, sm: 1 },
                }}
              >
                {isFetching ? 'Loading...' : 'Refresh'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  // ============================================
  // RENDER FUNCTIONS - Unclosed Shifts
  // ============================================

  const renderUnclosedShifts = () => {
    if (isFetching) {
      return (
        <Box sx={{ p: 2 }}>
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={100} sx={{ mb: 2, borderRadius: '12px' }} />
          ))}
        </Box>
      );
    }

    if (unclosedShifts.length === 0) {
      return (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>
          ✅ All shifts are properly closed! No pending shifts to close.
        </Alert>
      );
    }

    const paginatedShifts = unclosedShifts.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2, 
          flexWrap: 'wrap', 
          gap: 1 
        }}>
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            <HistoryIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 'inherit' }} />
            Unclosed Shifts ({unclosedShifts.length})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
            {unclosedShifts.filter(s => s.can_be_closed).length} ready to close • {unclosedShifts.filter(s => !s.can_be_closed).length} need attention
          </Typography>
        </Box>
        
        {paginatedShifts.map((shift) => (
          <Paper
            key={shift.shift_date}
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              mb: 2,
              borderRadius: '12px',
              border: `2px solid ${selectedShift?.shift_date === shift.shift_date ? LOCATION_COLOR : alpha('#000', 0.06)}`,
              bgcolor: selectedShift?.shift_date === shift.shift_date ? alpha(LOCATION_COLOR, 0.05) : 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: LOCATION_COLOR,
                boxShadow: theme.shadows[2],
                transform: 'translateY(-2px)',
              },
            }}
            onClick={() => {
              setSelectedShift(shift);
              setSelectedShiftDetails(shift);
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              justifyContent: 'space-between', 
              flexWrap: 'wrap', 
              gap: 1 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    bgcolor: shift.can_be_closed ? alpha('#4CAF50', 0.15) : alpha('#C62828', 0.15),
                    color: shift.can_be_closed ? '#4CAF50' : '#C62828',
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                  }}
                >
                  {shift.can_be_closed ? <CheckCircleIcon /> : <WarningAmberIcon />}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                    📅 {formatDate(shift.shift_date)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                    {shift.unpaid_orders_count} unpaid orders • {shift.payments_count} payments • {shift.payments_total > 0 ? formatCurrency(shift.payments_total) : '₦0.00'}
                  </Typography>
                  {shift.pending_orders_count > 0 && (
                    <Typography variant="caption" color="error" display="block" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                      ⚠️ {shift.pending_orders_count} pending orders
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                flexWrap: 'wrap',
                mt: { xs: 1, sm: 0 },
              }}>
                <Chip
                  size="small"
                  label={shift.can_be_closed ? "Ready to Close" : "Cannot Close"}
                  color={shift.can_be_closed ? "success" : "error"}
                  icon={shift.can_be_closed ? <CheckCircleIcon /> : <WarningAmberIcon />}
                  sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}
                />
                {shift.can_be_closed && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<DoneAllIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUnclosedShift(shift);
                      setUnclosedShiftDialogOpen(true);
                    }}
                    sx={{
                      fontSize: { xs: '0.55rem', sm: '0.75rem' },
                      minWidth: { xs: 'auto', sm: 80 },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    Close Shift
                  </Button>
                )}
                {selectedShift?.shift_date === shift.shift_date && (
                  <Chip size="small" label="Selected" color="primary" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }} />
                )}
              </Box>
            </Box>
          </Paper>
        ))}
        
        {unclosedShifts.length > rowsPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <TablePagination
              component="div"
              count={unclosedShifts.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                '& .MuiTablePagination-select': {
                  fontSize: { xs: '0.6rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-displayedRows': {
                  fontSize: { xs: '0.55rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-actions button': {
                  fontSize: { xs: '1.2rem', sm: '1.5rem' },
                },
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  // ============================================
  // RENDER FUNCTIONS - Shift Details
  // ============================================

  const renderShiftDetails = () => {
    if (!selectedShiftDetails) {
      return (
        <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Select a shift to view details
          </Typography>
        </Paper>
      );
    }

    const { pending_orders, unpaid_orders, payments, waiters, can_be_closed, cannot_close_message } = selectedShiftDetails;

    return (
      <Box>
        {/* Summary Cards */}
        <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 3, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2, 
            flexWrap: 'wrap', 
            gap: 1 
          }}>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
              Shift Details - {formatDate(selectedShiftDetails.shift_date)}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={isFetching}
              sx={{ fontSize: { xs: '0.65rem', sm: '0.875rem' } }}
            >
              Refresh
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: { xs: 1, sm: 1.5 } }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                  Total Orders
                </Typography>
                <Typography variant="h6" sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {selectedShiftDetails.payments_count}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: { xs: 1, sm: 1.5 } }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                  Total Revenue
                </Typography>
                <Typography variant="h6" color="success.main" sx={{ fontSize: { xs: '0.85rem', sm: '1.25rem' } }}>
                  {formatCurrency(selectedShiftDetails.payments_total)}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: { xs: 1, sm: 1.5 } }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                  Unpaid Orders
                </Typography>
                <Typography variant="h6" color={selectedShiftDetails.unpaid_orders_count > 0 ? 'error' : 'success'} sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {selectedShiftDetails.unpaid_orders_count}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: { xs: 1, sm: 1.5 } }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                  Pending Orders
                </Typography>
                <Typography variant="h6" color={selectedShiftDetails.pending_orders_count > 0 ? 'warning' : 'success'} sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                  {selectedShiftDetails.pending_orders_count || 0}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.6rem', sm: '0.875rem' },
                minHeight: { xs: 32, sm: 48 },
                px: { xs: 1, sm: 2 },
              }
            }}
          >
            <Tab label={`Payments (${payments?.length || 0})`} />
            <Tab label={`Unpaid Orders (${unpaid_orders?.length || 0})`} />
            <Tab label={`Pending Orders (${pending_orders?.length || 0})`} />
            <Tab label={`Waiters (${waiters?.length || 0})`} />
          </Tabs>
        </Box>

        {/* Payments Tab */}
        {activeTab === 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Payment #</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Order #</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Payment Method</strong></TableCell>
                  <TableCell align="right" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Amount</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Cashier</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Shift ID</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Time</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      No payments recorded for this day
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>{payment.payment_reference}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>{payment.order_number}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={payment.payment_method}
                          variant="outlined"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: { xs: '0.5rem', sm: '0.75rem' },
                            height: { xs: 20, sm: 24 },
                            bgcolor: payment.payment_method === 'Complimentary' ? '#FFF3E0' : 'transparent',
                            borderColor: payment.payment_method === 'Complimentary' ? '#FF9800' : undefined,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                        {payment.payment_method === 'Complimentary' ? '₦0.00' : formatCurrency(payment.amount)}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>{payment.processed_by_name}</TableCell>
                      <TableCell>
                        <Chip size="small" label={`#${payment.shift_id || 'N/A'}`} variant="outlined" color="info" 
                          sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.5rem', sm: '0.875rem' } }}>{formatDateTime(payment.payment_date)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Unpaid Orders Tab */}
      {/* Unpaid Orders Tab */}


{activeTab === 1 && (
  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Order #</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Shift ID</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Payment Status</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Type</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Customer</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Items</strong></TableCell>
          <TableCell align="right" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Amount</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Waiter</strong></TableCell>
          <TableCell align="center" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Action</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {unpaid_orders?.length === 0 ? (
          <TableRow>
            <TableCell colSpan={9} align="center">
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                No unpaid orders! All payments are recorded.
              </Alert>
            </TableCell>
          </TableRow>
        ) : (
          unpaid_orders.map((order) => {
            // ✅ ADD: Check for special statuses
            const isComplimentary = order.payment_status === 'COMPLIMENTARY';
            const isCancelled = order.payment_status === 'CANCELLED';
            const isRefunded = order.payment_status === 'REFUNDED';
            const isPaid = order.payment_status === 'PAID';
            const isSpecialStatus = isComplimentary || isCancelled || isRefunded || isPaid;
            
            // ✅ ADD: Get row background color
            const getRowBgColor = () => {
              if (isComplimentary) return alpha('#9C27B0', 0.05);
              if (isCancelled) return alpha('#F44336', 0.05);
              if (isRefunded) return alpha('#9C27B0', 0.05);
              if (isPaid) return alpha('#4CAF50', 0.05);
              return 'inherit';
            };
            
            // ✅ ADD: Get amount color
            const getAmountColor = () => {
              if (isComplimentary) return '#7B1FA2';
              if (isCancelled) return '#C62828';
              if (isRefunded) return '#4A148C';
              if (isPaid) return '#2E7D32';
              return 'error.main';
            };
            
            return (
              <TableRow 
                key={order.id} 
                hover
                sx={{ bgcolor: getRowBgColor() }}
              >
                <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>
                  {order.order_number}
                  {isSpecialStatus && (
                    <Chip 
                      size="small" 
                      label={isComplimentary ? 'Comp.' : isCancelled ? 'Canc.' : isRefunded ? 'Ref.' : 'Paid'} 
                      sx={{ 
                        ml: 1, 
                        fontSize: '0.5rem', 
                        height: 16,
                        bgcolor: isComplimentary ? '#9C27B0' : isCancelled ? '#F44336' : isRefunded ? '#9C27B0' : '#4CAF50',
                        color: 'white',
                      }} 
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={`#${order.shift_id || 'N/A'}`} variant="outlined" color="info"
                    sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                  />
                </TableCell>
                <TableCell>
                  {getStatusChip(order.payment_status, 'payment')}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={order.order_type} sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }} />
                </TableCell>
                <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>{order.customer_name}</TableCell>
                <TableCell>
                  {order.items?.slice(0, 2).map((item, i) => (
                    <Typography key={i} variant="caption" display="block" sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' } }}>
                      {item.quantity}x {item.name}
                    </Typography>
                  ))}
                  {order.items?.length > 2 && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' } }}>
                      +{order.items.length - 2} more
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ 
                  fontSize: { xs: '0.55rem', sm: '0.875rem' }, 
                  fontWeight: 'bold', 
                  color: getAmountColor(),
                }}>
                  {isComplimentary ? '₦0.00 (Comp.)' : formatCurrency(order.total_amount)}
                </TableCell>
                <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>{order.waiter_name}</TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    {/* Show Print button for all resolved statuses */}
                    {isSpecialStatus ? (
                      <Tooltip title={isComplimentary ? 'Print Complimentary Receipt' : isCancelled ? 'Print Cancelled Receipt' : isRefunded ? 'Print Refunded Receipt' : 'Print Receipt'}>
                        <Button
                          size="small"
                          variant="contained"
                          color={isComplimentary ? 'secondary' : isCancelled ? 'error' : isRefunded ? 'secondary' : 'success'}
                          startIcon={<PrintIcon />}
                         onClick={() => handlePrintReceiptAction(order)}
                          sx={{ 
                            minWidth: { xs: 'auto', sm: 80 },
                            px: { xs: 0.5, sm: 1.5 },
                            fontSize: { xs: '0.5rem', sm: '0.75rem' },
                            py: { xs: 0.25, sm: 0.5 },
                            bgcolor: isComplimentary ? '#9C27B0' : isCancelled ? '#F44336' : isRefunded ? '#9C27B0' : '#2E7D32',
                            '&:hover': { 
                              bgcolor: isComplimentary ? '#7B1FA2' : isCancelled ? '#D32F2F' : isRefunded ? '#7B1FA2' : '#1B5E20' 
                            },
                          }}
                        >
                          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            {isComplimentary ? 'Print Comp.' : isCancelled ? 'Print Canc.' : isRefunded ? 'Print Ref.' : 'Print'}
                          </Box>
                          <Box sx={{ display: { xs: 'block', sm: 'none' } }}><PrintIcon fontSize="small" /></Box>
                        </Button>
                      </Tooltip>
                    ) : (
                      // Show payment button for truly unpaid orders
                      <>
                        <Tooltip title="Print Receipt">
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<PrintIcon />}
                          onClick={() => handlePrintReceiptAction(order)}
                            sx={{ 
                              minWidth: { xs: 'auto', sm: 80 },
                              px: { xs: 0.5, sm: 1.5 },
                              fontSize: { xs: '0.5rem', sm: '0.75rem' },
                              py: { xs: 0.25, sm: 0.5 },
                            }}
                          >
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Print</Box>
                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}><PrintIcon fontSize="small" /></Box>
                          </Button>
                        </Tooltip>
                        <Tooltip title="Record Payment">
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<PaymentIcon />}
                            onClick={() => {
                              setSelectedUnpaidOrder(order);
                              setSelectedPaymentMethod(PAYMENT_METHOD_IDS.CASH);
                              setPaymentDialogOpen(true);
                            }}
                            sx={{ 
                              minWidth: { xs: 'auto', sm: 80 },
                              px: { xs: 0.5, sm: 1.5 },
                              fontSize: { xs: '0.5rem', sm: '0.75rem' },
                              py: { xs: 0.25, sm: 0.5 },
                            }}
                          >
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Pay</Box>
                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}><PaymentIcon fontSize="small" /></Box>
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </TableContainer>


        )}

      
 {/* Pending Orders Tab */}
{activeTab === 2 && (
  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Order #</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Shift ID</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Status</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Payment Status</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Type</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Customer</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Items</strong></TableCell>
          <TableCell align="right" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Amount</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Waiter</strong></TableCell>
          <TableCell align="center" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Action</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {pending_orders?.length === 0 ? (
          <TableRow>
            <TableCell colSpan={10} align="center">
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                No pending orders!
              </Alert>
            </TableCell>
          </TableRow>
        ) : (
          pending_orders
            // ✅ FIX: Filter out PAID and COMPLIMENTARY orders from pending tab
            .filter(order => order.payment_status !== 'PAID' && order.payment_status !== 'COMPLIMENTARY')
            .map((order) => {
              // ✅ Check if order is already PAID or COMPLIMENTARY
              const isPaid = order.payment_status === 'PAID';
              const isComplimentary = order.payment_status === 'COMPLIMENTARY';
              const isPartial = order.payment_status === 'PARTIAL';
              
              return (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>
                    {order.order_number}
                    {isComplimentary && (
                      <Chip 
                        size="small" 
                        label="Comp." 
                        sx={{ ml: 1, fontSize: '0.5rem', height: 16, bgcolor: '#9C27B0', color: 'white' }} 
                      />
                    )}
                    {isPaid && (
                      <Chip 
                        size="small" 
                        label="Paid" 
                        sx={{ ml: 1, fontSize: '0.5rem', height: 16, bgcolor: '#4CAF50', color: 'white' }} 
                      />
                    )}
                    {isPartial && (
                      <Chip 
                        size="small" 
                        label="Partial" 
                        sx={{ ml: 1, fontSize: '0.5rem', height: 16, bgcolor: '#FF6F00', color: 'white' }} 
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={`#${order.shift_id || 'N/A'}`} variant="outlined" color="info"
                      sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                    />
                  </TableCell>
                  <TableCell>
                    {getStatusChip(order.order_status, 'order')}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(order.payment_status, 'payment')}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={order.order_type} sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>{order.customer_name}</TableCell>
                  <TableCell>
                    {order.items?.slice(0, 2).map((item, i) => (
                      <Typography key={i} variant="caption" display="block" sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' } }}>
                        {item.quantity}x {item.name}
                      </Typography>
                    ))}
                    {order.items?.length > 2 && (
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' } }}>
                        +{order.items.length - 2} more
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>
                    {formatCurrency(order.total_amount)}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>{order.waiter_name}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      {/* ✅ For PAID or COMPLIMENTARY orders, only show Print button */}
                      {(isPaid || isComplimentary) ? (
                        <Tooltip title={isComplimentary ? 'Print Complimentary Receipt' : 'Print Receipt'}>
                          <Button
                            size="small"
                            variant="contained"
                            color={isComplimentary ? 'secondary' : 'success'}
                            startIcon={<PrintIcon />}
                            onClick={() => handlePrintReceiptAction(order)}
                            sx={{ 
                              minWidth: { xs: 'auto', sm: 80 },
                              px: { xs: 0.5, sm: 1.5 },
                              fontSize: { xs: '0.5rem', sm: '0.75rem' },
                              py: { xs: 0.25, sm: 0.5 },
                              bgcolor: isComplimentary ? '#9C27B0' : '#2E7D32',
                              '&:hover': { 
                                bgcolor: isComplimentary ? '#7B1FA2' : '#1B5E20' 
                              },
                            }}
                          >
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                              {isComplimentary ? 'Print Comp.' : 'Print'}
                            </Box>
                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}><PrintIcon fontSize="small" /></Box>
                          </Button>
                        </Tooltip>
                      ) : (
                        /* ✅ For UNPAID or PARTIAL orders, show Pay & Complete button */
                        <Tooltip title={isPartial ? 'Complete Payment' : 'Pay & Complete Order'}>
                          <Button
                            size="small"
                            variant="contained"
                            color={isPartial ? 'warning' : 'success'}
                            startIcon={<PaymentIcon />}
                            onClick={() => {
                              setSelectedPendingOrder(order);
                              setSelectedPaymentMethod(PAYMENT_METHOD_IDS.CASH);
                              setCompleteAndPayDialogOpen(true);
                            }}
                            sx={{ 
                              minWidth: { xs: 'auto', sm: 100 },
                              px: { xs: 0.5, sm: 1.5 },
                              fontSize: { xs: '0.5rem', sm: '0.75rem' },
                              py: { xs: 0.25, sm: 0.5 },
                              bgcolor: isPartial ? '#FF6F00' : '#4CAF50',
                              '&:hover': { 
                                bgcolor: isPartial ? '#E65100' : '#388E3C' 
                              },
                            }}
                          >
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                              {isPartial ? 'Pay Balance' : 'Pay & Complete'}
                            </Box>
                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}><PaymentIcon fontSize="small" /></Box>
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })
        )}
      </TableBody>
    </Table>
  </TableContainer>
)}

{/* Waiters Tab */}
{activeTab === 3 && (
  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Waiter Name</strong></TableCell>
          <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Waiter ID</strong></TableCell>
          <TableCell align="center" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Orders</strong></TableCell>
          <TableCell align="right" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}><strong>Revenue</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {waiters?.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4} align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
              No waiters recorded for this day
            </TableCell>
          </TableRow>
        ) : (
          waiters.map((waiter) => (
            <TableRow key={waiter.waiter_id} hover>
              <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>{waiter.waiter_name}</TableCell>
              <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' } }}>{waiter.waiter_id}</TableCell>
              <TableCell align="center">
                <Chip size="small" label={waiter.order_count} color="primary" 
                  sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                />
              </TableCell>
              <TableCell align="right" sx={{ fontSize: { xs: '0.55rem', sm: '0.875rem' }, fontWeight: 'bold' }}>
                {formatCurrency(waiter.total_revenue)}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
)}

       
      </Box>
    );
  };

  // ============================================
  // RENDER FUNCTIONS - Close Day Section
  // ============================================

  const renderCloseDaySection = () => {
    if (isDayClosed) return null;

    return (
      <Box id="close-day-section">
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, bgcolor: canClose ? '#E8F5E9' : '#FFEBEE' }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
            {canClose ? '✅ Day Ready to Close' : '⏳ Day Cannot Be Closed Yet'}
          </Typography>

          {!canClose && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <strong>Please resolve the following before closing:</strong>
                {status?.active_orders_count > 0 && (
                  <li>{status.active_orders_count} active order(s) need to be completed or cancelled</li>
                )}
                {status?.unpaid_orders_count > 0 && (
                  <li>{status.unpaid_orders_count} completed order(s) without payment recorded</li>
                )}
                {status?.pending_orders_count > 0 && (
                  <li>{status.pending_orders_count} pending order(s) need to be completed</li>
                )}
              </Typography>
            </Alert>
          )}

          {canClose && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                ✅ No Pending Order. You can now close the day.
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="Closure Reason (Optional)"
            value={closureReason}
            onChange={(e) => setClosureReason(e.target.value)}
            placeholder="E.g., End of day, Shift change, etc."
            sx={{ mb: 2 }}
            disabled={!canClose}
            InputProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />

          <TextField
            fullWidth
            size="small"
            label="Type 'CLOSE DAY' to confirm"
            value={confirmCloseText}
            onChange={(e) => setConfirmCloseText(e.target.value)}
            error={confirmCloseText !== '' && confirmCloseText !== 'CLOSE DAY'}
            helperText={confirmCloseText !== '' && confirmCloseText !== 'CLOSE DAY' ? 'Please type CLOSE DAY exactly' : ''}
            sx={{ mb: 2 }}
            disabled={!canClose}
            InputProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />

          <Button
            fullWidth
            variant="contained"
            color="error"
            size="large"
            startIcon={closingDay ? <CircularProgress size={20} /> : <LockClockIcon />}
            onClick={handleCloseDay}
            disabled={!canClose || closingDay || confirmCloseText !== 'CLOSE DAY'}
            sx={{
              py: { xs: 1.25, sm: 1.5 },
              fontSize: { xs: '0.8rem', sm: '1rem' },
              background: canClose
                ? 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)'
                : 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
              '&:hover': {
                transform: canClose ? 'scale(1.02)' : 'none',
                background: canClose
                  ? 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)'
                  : 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)'
              }
            }}
          >
            {closingDay ? 'Closing Day...' : 'Confirm Close Day'}
          </Button>
        </Paper>
      </Box>
    );
  };

  // ============================================
  // DIALOGS
  // ============================================

  const renderPaymentDialog = () => {
  if (!selectedUnpaidOrder) return null;
  
  const orderTotal = selectedUnpaidOrder.total_amount || 0;
  const isMixed = selectedPaymentMethod === 'MIXED';
  
  return (
    <Dialog
      open={paymentDialogOpen}
      onClose={() => {
        setPaymentDialogOpen(false);
        setSelectedUnpaidOrder(null);
        setSelectedPaymentMethod('CASH');
        setPaymentAmount('');
        setCashAmount('');
        setTransferAmount('');
        setCardAmount('');
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: { xs: 1, sm: 2 },
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        bgcolor: alpha(LOCATION_COLOR, 0.03),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon sx={{ color: LOCATION_COLOR }} />
          <Typography variant="h6" component="span" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
            Record Payment - {selectedUnpaidOrder.order_number}
          </Typography>
        </Box>
        <IconButton onClick={() => {
          setPaymentDialogOpen(false);
          setSelectedUnpaidOrder(null);
          setSelectedPaymentMethod('CASH');
          setPaymentAmount('');
          setCashAmount('');
          setTransferAmount('');
          setCardAmount('');
        }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 3,
            bgcolor: alpha(LOCATION_COLOR, 0.05),
            borderRadius: '12px',
            border: `1px solid ${alpha(LOCATION_COLOR, 0.12)}`,
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                Order #
              </Typography>
              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                {selectedUnpaidOrder.order_number}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                Total Amount
              </Typography>
              <Typography variant="h6" fontWeight={700} color={LOCATION_COLOR} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {formatCurrency(orderTotal)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                Date of Original Order
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                {formatDateTime(selectedUnpaidOrder.order_time)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                Shift ID
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                {selectedUnpaidOrder.shift_id || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>
          Select Payment Method
        </Typography>
        
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {PAYMENT_METHODS.map((method) => (
            <Grid item xs={6} sm={4} key={method.value}>
              <Paper
                elevation={0}
                onClick={() => {
                  setSelectedPaymentMethod(method.value);
                  if (method.value !== 'MIXED') {
                    setPaymentAmount(orderTotal.toString());
                    setCashAmount('');
                    setTransferAmount('');
                    setCardAmount('');
                  } else {
                    const total = orderTotal;
                    const third = (total / 3).toFixed(2);
                    setCashAmount(third);
                    setTransferAmount(third);
                    setCardAmount(third);
                    setPaymentAmount(total.toString());
                  }
                }}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '12px',
                  border: `2px solid ${selectedPaymentMethod === method.value ? method.color : alpha('#000', 0.08)}`,
                  bgcolor: selectedPaymentMethod === method.value ? alpha(method.color, 0.08) : 'white',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: theme.shadows[2],
                  },
                }}
              >
               <Box sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, color: method.color }}>
  {method.icon}
</Box>
   <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' }, mt: 0.5 }}>
                  {method.label}
                </Typography>
                {selectedPaymentMethod === method.value && (
                  <CheckCircleIcon sx={{ color: method.color, fontSize: '1rem', position: 'absolute', top: 4, right: 4 }} />
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
        
        {selectedPaymentMethod !== 'COMPLIMENTARY' && selectedPaymentMethod !== 'MIXED' && (
          <TextField
            fullWidth
            label="Payment Amount"
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
            helperText={`Full amount: ${formatCurrency(orderTotal)}`}
          />
        )}
        
        {isMixed && (
          <Box sx={{ mt: 2, p: 2, bgcolor: alpha('#FF6F00', 0.05), borderRadius: '12px', border: `1px solid ${alpha('#FF6F00', 0.15)}` }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: '0.85rem', color: '#FF6F00' }}>
              Split Payment Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cash"
                  type="number"
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Transfer"
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Card"
                  type="number"
                  value={cardAmount}
                  onChange={(e) => setCardAmount(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: alpha('#FF6F00', 0.08), borderRadius: '8px' }}>
              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                Total:
              </Typography>
              <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.9rem', color: '#FF6F00' }}>
                {formatCurrency((parseFloat(cashAmount) || 0) + (parseFloat(transferAmount) || 0) + (parseFloat(cardAmount) || 0))}
              </Typography>
            </Box>
          </Box>
        )}
        
        {selectedPaymentMethod === 'COMPLIMENTARY' && (
          <Alert
            severity="warning"
            sx={{ mt: 2, borderRadius: '10px' }}
            icon={<ComplimentaryIcon />}
          >
            <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
              <strong>Complimentary Order</strong> - No payment will be recorded.
              The amount will be set to ₦0.00 in the payments table.
            </Typography>
          </Alert>
        )}
        
        {paymentSuccess && (
          <Alert
            severity="success"
            sx={{ mt: 2, borderRadius: '10px' }}
            icon={<CheckCircleIcon />}
          >
            Payment recorded successfully!
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{
        borderTop: `1px solid ${alpha('#000', 0.06)}`,
        pt: 2,
        px: { xs: 2, sm: 3 },
        pb: { xs: 2, sm: 3 },
        gap: 1,
        flexWrap: 'wrap',
      }}>
        <Button
          onClick={() => {
            setPaymentDialogOpen(false);
            setSelectedUnpaidOrder(null);
            setSelectedPaymentMethod('CASH');
            setPaymentAmount('');
            setCashAmount('');
            setTransferAmount('');
            setCardAmount('');
          }}
          disabled={isProcessingPayment}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleRecordPayment}
          variant="contained"
          disabled={isProcessingPayment || paymentSuccess}
          sx={{
            bgcolor: LOCATION_COLOR,
            '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
            '&:disabled': { bgcolor: 'grey.300' },
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.25 },
            fontSize: { xs: '0.75rem', sm: '0.95rem' },
          }}
          startIcon={isProcessingPayment ? <CircularProgress size={22} color="inherit" /> : <PaymentIcon />}
        >
          {isProcessingPayment ? 'Processing...' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

  const renderUnclosedShiftDialog = () => {
    if (!selectedUnclosedShift) return null;
    
    return (
      <Dialog
        open={unclosedShiftDialogOpen}
        onClose={() => setUnclosedShiftDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: alpha('#ED6C02', 0.05),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CleaningIcon sx={{ color: '#ED6C02' }} />
            <Typography variant="h6" component="span" fontWeight={700}>
              Close Unclosed Shift
            </Typography>
          </Box>
          <IconButton onClick={() => setUnclosedShiftDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
            <Typography variant="body2">
              You are about to close the shift for <strong>{formatDate(selectedUnclosedShift.shift_date)}</strong>.
              This will record a day closure for this date and mark the shift as closed.
            </Typography>
          </Alert>
          
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: 3,
              bgcolor: alpha('#000', 0.02),
              borderRadius: '12px',
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Shift Date
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {formatDate(selectedUnclosedShift.shift_date)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Staff
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {selectedUnclosedShift.shift_details?.staff_name || 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Revenue
                </Typography>
                <Typography variant="body1" fontWeight={700} color="success.main">
                  {formatCurrency(selectedUnclosedShift.payments_total || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Orders
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {selectedUnclosedShift.payments_count || 0}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          
          <TextField
            fullWidth
            size="small"
            label="Closure Reason (Optional)"
            placeholder="Reason for closing this shift"
            value={closureReason}
            onChange={(e) => setClosureReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <Alert severity="warning" sx={{ borderRadius: '10px' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              <strong>⚠️ This action cannot be undone.</strong><br />
              Please ensure all orders for this date are properly completed and paid.
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          gap: 1,
          flexWrap: 'wrap',
        }}>
          <Button
            onClick={() => setUnclosedShiftDialogOpen(false)}
            disabled={isClosingUnclosedShift}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleCloseUnclosedShift(selectedUnclosedShift.shift_date)}
            variant="contained"
            color="success"
            disabled={isClosingUnclosedShift}
            startIcon={isClosingUnclosedShift ? <CircularProgress size={20} color="inherit" /> : <DoneAllIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: 4,
              py: 1.25,
            }}
          >
            {isClosingUnclosedShift ? 'Closing...' : 'Close Shift'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

 const renderCompleteAndPayDialog = () => {
  if (!selectedPendingOrder) return null;
  
  // ✅ FIX: Check if order is PARTIAL
  const isPartial = selectedPendingOrder.payment_status === 'PARTIAL';
  const remainingBalance = isPartial 
    ? (selectedPendingOrder.total_amount || 0) - (selectedPendingOrder.total_paid || 0)
    : selectedPendingOrder.total_amount;
  
  // ✅ FIX: Check if order is already PAID
  const isPaid = selectedPendingOrder.payment_status === 'PAID';
  
  return (
    <Dialog
      open={completeAndPayDialogOpen}
      onClose={() => {
        setCompleteAndPayDialogOpen(false);
        setSelectedPendingOrder(null);
        setSelectedPaymentMethod('CASH');
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: { xs: 1, sm: 2 },
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        bgcolor: isPartial ? alpha('#FF6F00', 0.05) : isPaid ? alpha('#4CAF50', 0.05) : alpha('#4CAF50', 0.05),
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon sx={{ color: isPartial ? '#FF6F00' : isPaid ? '#4CAF50' : '#4CAF50' }} />
          <Typography variant="h6" component="span" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
            {isPartial ? 'Complete Payment - ' : isPaid ? 'Order Already Paid - ' : 'Complete & Pay - '}
            {selectedPendingOrder.order_number}
          </Typography>
        </Box>
        <IconButton onClick={() => {
          setCompleteAndPayDialogOpen(false);
          setSelectedPendingOrder(null);
          setSelectedPaymentMethod('CASH');
        }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {isPaid ? (
          <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }}>
            <Typography variant="body2">
              This order is already <strong>PAID</strong>. You can print the receipt.
            </Typography>
          </Alert>
        ) : isPartial ? (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: '10px' }}>
            <Typography variant="body2">
              This order has a <strong>PARTIAL</strong> payment. 
              Remaining balance: <strong>{formatCurrency(remainingBalance)}</strong>
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3, borderRadius: '10px' }}>
            <Typography variant="body2">
              This will <strong>complete</strong> the order and <strong>record payment</strong> in one action.
              The order will move from PENDING to COMPLETED and PAID status.
            </Typography>
          </Alert>
        )}
        
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 3,
            bgcolor: isPartial ? alpha('#FF6F00', 0.05) : isPaid ? alpha('#4CAF50', 0.05) : alpha('#4CAF50', 0.05),
            borderRadius: '12px',
            border: `1px solid ${isPartial ? alpha('#FF6F00', 0.12) : isPaid ? alpha('#4CAF50', 0.12) : alpha('#4CAF50', 0.12)}`,
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                Order #
              </Typography>
              <Typography variant="body1" fontWeight={700} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                {selectedPendingOrder.order_number}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                Total Amount
              </Typography>
              <Typography variant="h6" fontWeight={700} color={isPartial ? '#FF6F00' : '#4CAF50'} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {formatCurrency(selectedPendingOrder.total_amount)}
              </Typography>
            </Grid>
            {isPartial && (
              <>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                    Already Paid
                  </Typography>
                  <Typography variant="body1" fontWeight={700} color="#2E7D32" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                    {formatCurrency(selectedPendingOrder.total_paid || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                    Remaining Balance
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="#C62828" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                    {formatCurrency(remainingBalance)}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                Payment Status
              </Typography>
              <Chip 
                size="small" 
                label={selectedPendingOrder.payment_status} 
                color={isPartial ? 'warning' : isPaid ? 'success' : 'error'}
                sx={{ mt: 0.5 }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                Waiter
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                {selectedPendingOrder.waiter_name || 'Unknown'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                Table
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                {selectedPendingOrder.table_name || 'Takeaway'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        
        {!isPaid && (
          <>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>
              Select Payment Method
            </Typography>
            
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {PAYMENT_METHODS.filter(m => m.value !== 'MIXED').map((method) => (
                <Grid item xs={6} sm={4} key={method.value}>
                  <Paper
                    elevation={0}
                    onClick={() => setSelectedPaymentMethod(method.value)}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: '12px',
                      border: `2px solid ${selectedPaymentMethod === method.value ? method.color : alpha('#000', 0.08)}`,
                      bgcolor: selectedPaymentMethod === method.value ? alpha(method.color, 0.08) : 'white',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: theme.shadows[2],
                      },
                    }}
                  >
                    <Box sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, color: method.color }}>
                      {method.icon}
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' }, mt: 0.5 }}>
                      {method.label}
                    </Typography>
                    {selectedPaymentMethod === method.value && (
                      <CheckCircleIcon sx={{ color: method.color, fontSize: '1rem', position: 'absolute', top: 4, right: 4 }} />
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            {selectedPaymentMethod === 'COMPLIMENTARY' && (
              <Alert
                severity="warning"
                sx={{ mt: 2, borderRadius: '10px' }}
                icon={<ComplimentaryIcon />}
              >
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  <strong>Complimentary Order</strong> - The order will be marked as COMPLIMENTARY with ₦0.00 payment.
                </Typography>
              </Alert>
            )}
            
            {isPartial && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                  Amount to be paid: <strong>{formatCurrency(remainingBalance)}</strong> (remaining balance)
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{
        borderTop: `1px solid ${alpha('#000', 0.06)}`,
        pt: 2,
        px: { xs: 2, sm: 3 },
        pb: { xs: 2, sm: 3 },
        gap: 1,
        flexWrap: 'wrap',
      }}>
        <Button
          onClick={() => {
            setCompleteAndPayDialogOpen(false);
            setSelectedPendingOrder(null);
            setSelectedPaymentMethod('CASH');
          }}
          disabled={isCompletingAndPaying}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          {isPaid ? 'Close' : 'Cancel'}
        </Button>
        {isPaid ? (
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setCompleteAndPayDialogOpen(false);
              setSelectedPendingOrder(null);
              handlePrintReceiptAction(selectedPendingOrder);
            }}
            startIcon={<PrintIcon />}
            sx={{
              bgcolor: '#2E7D32',
              '&:hover': { bgcolor: '#1B5E20' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: { xs: 3, sm: 4 },
              py: { xs: 1, sm: 1.25 },
              fontSize: { xs: '0.75rem', sm: '0.95rem' },
            }}
          >
            Print Receipt
          </Button>
        ) : (
          <Button
            onClick={handleCompleteAndPay}
            variant="contained"
            disabled={isCompletingAndPaying}
            sx={{
              bgcolor: isPartial ? '#FF6F00' : '#4CAF50',
              '&:hover': { bgcolor: isPartial ? '#E65100' : '#388E3C' },
              '&:disabled': { bgcolor: 'grey.300' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: { xs: 3, sm: 4 },
              py: { xs: 1, sm: 1.25 },
              fontSize: { xs: '0.75rem', sm: '0.95rem' },
            }}
            startIcon={isCompletingAndPaying ? <CircularProgress size={22} color="inherit" /> : <CheckCircleIcon />}
          >
            {isCompletingAndPaying ? 'Processing...' : isPartial ? 'Complete Payment' : 'Complete & Pay'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const renderReceiptDialog = () => {
  if (!receiptData) return null;
  
  const receiptHTML = generateThermalReceipt(receiptData);
  
  // ✅ UPDATED: Check if order is resolved
  const resolvedStatuses = ['PAID', 'REFUNDED', 'COMPLIMENTARY', 'CANCELLED'];
  const isResolved = resolvedStatuses.includes(receiptData.payment_status);
  
  // ✅ ADD: Get dialog title based on status
  const getDialogTitle = () => {
    switch(receiptData.payment_status) {
      case 'COMPLIMENTARY': return 'Complimentary Receipt';
      case 'CANCELLED': return 'Cancelled Order Receipt';
      case 'REFUNDED': return 'Refunded Order Receipt';
      case 'PAID': return 'Payment Receipt';
      default: return 'Backdated Payment Receipt';
    }
  };
  
  // ✅ ADD: Get button color based on status
  const getButtonColor = () => {
    switch(receiptData.payment_status) {
      case 'COMPLIMENTARY': return '#9C27B0';
      case 'CANCELLED': return '#F44336';
      case 'REFUNDED': return '#9C27B0';
      case 'PAID': return '#2E7D32';
      default: return '#2E7D32';
    }
  };
  
  // ✅ ADD: Get button hover color
  const getButtonHoverColor = () => {
    switch(receiptData.payment_status) {
      case 'COMPLIMENTARY': return '#7B1FA2';
      case 'CANCELLED': return '#D32F2F';
      case 'REFUNDED': return '#7B1FA2';
      case 'PAID': return '#1B5E20';
      default: return '#1B5E20';
    }
  };
  
  return (
    <Dialog
      open={receiptDialogOpen}
      onClose={handleCloseReceiptDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: { xs: 1, sm: 2 },
          maxWidth: '420px',
        },
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        bgcolor: '#f8f9fa',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ color: LOCATION_COLOR }} />
          <Typography variant="h6" component="span" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
            {getDialogTitle()}
          </Typography>
        </Box>
        <IconButton onClick={handleCloseReceiptDialog} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, bgcolor: '#f5f7fa' }}>
        {isReceiptPrinting && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                Preparing print...
              </Typography>
              <CircularProgress size={20} />
            </Box>
            <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
          </Box>
        )}
        
        {!isResolved ? (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
            This order has not been resolved yet. Please process payment first.
          </Alert>
        ) : (
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'white', borderRadius: '8px', border: `1px solid ${alpha('#000', 0.06)}`, overflow: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: receiptHTML }} />
          </Paper>
        )}
      </DialogContent>
      
      <DialogActions sx={{
        borderTop: `1px solid ${alpha('#000', 0.06)}`,
        pt: 2,
        px: { xs: 2, sm: 3 },
        pb: { xs: 2, sm: 3 },
        gap: 1,
        bgcolor: '#f8f9fa',
        flexWrap: 'wrap',
      }}>
        <Button
          onClick={handleCloseReceiptDialog}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          Close
        </Button>
        {isResolved && (
          <Button
            variant="contained"
            onClick={() => handlePrintReceiptAction(receiptData)}
            disabled={isReceiptPrinting}
            startIcon={isReceiptPrinting ? <CircularProgress size={20} /> : <PrintIcon />}
            sx={{
              bgcolor: getButtonColor(),
              '&:hover': { bgcolor: getButtonHoverColor() },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: { xs: 3, sm: 4 },
              fontSize: { xs: '0.75rem', sm: '0.95rem' },
            }}
          >
            {isReceiptPrinting ? 'Printing...' : 
              receiptData?.payment_status === 'COMPLIMENTARY' ? 'Print Comp. Receipt' :
              receiptData?.payment_status === 'CANCELLED' ? 'Print Cancelled Receipt' :
              receiptData?.payment_status === 'REFUNDED' ? 'Print Refunded Receipt' : 'Print Receipt'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

  const renderActionRequiredDialog = () => {
    if (!actionRequired) return null;
    
    const isPendingOrders = actionRequired.type === 'pending_orders';
    const orders = actionRequired.data || [];
    
    return (
      <Dialog
        open={!!actionRequired}
        onClose={() => setActionRequired(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: alpha('#ED6C02', 0.05),
        }}>
          <WarningAmberIcon sx={{ color: '#ED6C02' }} />
          <Typography variant="h6" fontWeight={700}>
            {isPendingOrders ? 'Pending Orders Found' : 'Unpaid Orders Found'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
            {actionRequired.message}
          </Alert>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isPendingOrders 
              ? 'These orders need to be completed or cancelled before starting a new day:'
              : 'These orders need payment before starting a new day:'
            }
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Order #</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell align="center"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>{order.order_number}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={order.order_status || 'PENDING'} 
                        color={isPendingOrders ? 'warning' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(order.total_amount)}
                    </TableCell>
                    <TableCell align="center">
                      {isPendingOrders ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => {
                            window.location.href = `/${locationKey}-cashier/orders`;
                          }}
                        >
                          Manage
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => {
                            setSelectedUnpaidOrder(order);
                            setActionRequired(null);
                            setPaymentDialogOpen(true);
                          }}
                        >
                          Pay Now
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (isPendingOrders) {
                  window.location.href = `/${locationKey}-cashier/orders`;
                } else {
                  setActionRequired(null);
                  setPaymentDialogOpen(true);
                }
              }}
              startIcon={<PlayArrowIcon />}
            >
              {isPendingOrders ? 'Manage Orders' : 'Record Payments'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setActionRequired(null)}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  const renderStatsDialog = () => {
    if (!stats) return null;
    
    return (
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, sm: 2 },
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: alpha(LOCATION_COLOR, 0.03),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon sx={{ color: LOCATION_COLOR }} />
            <Typography variant="h6" component="span" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              EOD Statistics
            </Typography>
          </Box>
          <IconButton onClick={() => setStatsDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '12px',
                  border: `1px solid ${alpha('#1976D2', 0.1)}`,
                  bgcolor: alpha('#1976D2', 0.03),
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                  Today's Orders
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#1976D2', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                  {stats.today_orders || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '12px',
                  border: `1px solid ${alpha('#2E7D32', 0.1)}`,
                  bgcolor: alpha('#2E7D32', 0.03),
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                  Today's Revenue
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#2E7D32', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                  {formatCurrency(stats.today_revenue || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '12px',
                  border: `1px solid ${alpha('#C62828', 0.1)}`,
                  bgcolor: alpha('#C62828', 0.03),
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                  Unpaid Orders
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#C62828', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                  {stats.unpaid_orders || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '12px',
                  border: `1px solid ${alpha('#E65100', 0.1)}`,
                  bgcolor: alpha('#E65100', 0.03),
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                  Active Orders
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#E65100', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                  {stats.active_orders || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '12px',
                  border: `1px solid ${alpha('#4A148C', 0.1)}`,
                  bgcolor: alpha('#4A148C', 0.03),
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                  Completed Orders
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#4A148C', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                  {stats.completed_orders || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '12px',
                  border: `1px solid ${alpha('#9E9E9E', 0.1)}`,
                  bgcolor: alpha('#9E9E9E', 0.03),
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' } }}>
                  Day Status
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: isDayClosed ? '#D32F2F' : '#4CAF50', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                  {isDayClosed ? 'Closed' : 'Open'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {stats.current_shift && (
            <Box sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, bgcolor: alpha(LOCATION_COLOR, 0.05), borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>
                Current Shift Information
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>Staff</Typography>
                  <Typography variant="body2" fontWeight="600" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{stats.current_shift.staff_name}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>Shift Date</Typography>
                  <Typography variant="body2" fontWeight="600" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{formatDate(stats.current_shift.shift_date)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>Status</Typography>
                  <Chip 
                    size="small" 
                    label={stats.current_shift.is_closed ? 'Closed' : 'Open'} 
                    color={stats.current_shift.is_closed ? 'error' : 'success'}
                    sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>Cashier Closed</Typography>
                  <Chip 
                    size="small" 
                    label={stats.current_shift.is_cashier_closed ? 'Yes' : 'No'} 
                    color={stats.current_shift.is_cashier_closed ? 'error' : 'success'}
                    sx={{ fontSize: { xs: '0.5rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
        }}>
          <Button
            onClick={() => setStatsDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ============================================
  // LOADING STATE
  // ============================================
  
  if (isLoading) {
    return <Preloader text={`Loading ${LOCATION_NAME} EOD...`} size={70} fullScreen={true} />;
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <Suspense fallback={<Preloader text="Loading..." size={50} fullScreen={true} />}>
      <div className="epos-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div className="epos-main-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <SidebarComponent activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
          
          <div className="epos-main" style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <Header />
            
            <Box sx={{
              display: 'flex',
              flex: 1,
              overflow: 'hidden',
              position: 'relative',
            }}>
              <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: { xs: 1.5, sm: 2, md: 3 },
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'grey.300',
                  borderRadius: 3,
                },
              }}>
                {/* Hero Section */}
                <Box sx={{
                  bgcolor: 'white',
                  borderRadius: '16px',
                  p: { xs: 1.5, sm: 2.5, md: 3 },
                  mb: { xs: 1.5, sm: 2.5, md: 3 },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexWrap: 'wrap',
                  gap: 2,
                  border: `1px solid ${alpha(LOCATION_COLOR, 0.15)}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}>
                  <Box>
                    <Typography variant="h4" sx={{
                      color: LOCATION_COLOR,
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', sm: '1.8rem', md: '2.125rem' },
                    }}>
                      {displayText}
                      <span style={{
                        display: 'inline-block',
                        width: '3px',
                        height: { xs: '22px', sm: '28px' },
                        backgroundColor: LOCATION_COLOR,
                        marginLeft: '4px',
                        verticalAlign: 'text-bottom',
                        animation: 'blink 1s step-end infinite',
                      }} />
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                      {isDayClosed ? '🔒 Day is closed' : needsSkipHandling ? '⏳ System idle - skipped days' : canClose ? '✅ Ready to close' : '⏳ Day in progress'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 1, sm: 1.5 }, 
                    flexWrap: 'wrap',
                    width: { xs: '100%', sm: 'auto' },
                  }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setStatsDialogOpen(true)}
                      startIcon={<AssessmentIcon />}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: { xs: '0.6rem', sm: '0.8rem' },
                        borderColor: LOCATION_COLOR,
                        color: LOCATION_COLOR,
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 0.5, sm: 0.75 },
                        flex: { xs: 1, sm: 'none' },
                        '&:hover': {
                          borderColor: LOCATION_COLOR,
                          bgcolor: alpha(LOCATION_COLOR, 0.08),
                        },
                      }}
                    >
                      Stats
                    </Button>
                    {isDayClosed && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleViewReport}
                        startIcon={isFetching ? <CircularProgress size={18} color="inherit" /> : <ReceiptLongIcon />}
                        sx={{
                          bgcolor: '#2E7D32',
                          '&:hover': { bgcolor: '#1B5E20' },
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: { xs: '0.6rem', sm: '0.8rem' },
                          borderRadius: '10px',
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 0.5, sm: 0.75 },
                          flex: { xs: 1, sm: 'none' },
                        }}
                        disabled={isFetching}
                      >
                        {isFetching ? 'Loading...' : 'EOD Report'}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleRefresh}
                      startIcon={isFetching ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                      sx={{
                        bgcolor: LOCATION_COLOR,
                        '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: { xs: '0.6rem', sm: '0.8rem' },
                        borderRadius: '10px',
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 0.5, sm: 0.75 },
                        flex: { xs: 1, sm: 'none' },
                      }}
                      disabled={isFetching}
                    >
                      {isFetching ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </Box>
                </Box>

                {/* Stats Summary */}
                {renderStatsSummary()}

                {/* Status Section */}
                {renderStatusSection()}

                {/* Unclosed Shifts Section */}
                {unclosedShifts.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Paper sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, borderRadius: 2 }}>
                      {renderUnclosedShifts()}
                    </Paper>
                  </Box>
                )}

                {/* Shift Details Section */}
                {selectedShift && (
                  <Box sx={{ mb: 3 }}>
                    <Paper sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, borderRadius: 2 }}>
                      {renderShiftDetails()}
                    </Paper>
                  </Box>
                )}

                {/* Close Day Section */}
                {renderCloseDaySection()}
              </Box>
            </Box>
          </div>
        </div>
        <Footer />
      </div>

      {/* Dialogs */}
      {renderPaymentDialog()}
      {renderUnclosedShiftDialog()}
      {renderCompleteAndPayDialog()} 
      {renderReceiptDialog()}
      {renderStatsDialog()}
      {renderActionRequiredDialog()}

      {/* EOD Report Dialog */}
      <EODReport
        open={eodReportOpen}
        onClose={handleEODReportClose}
        locationId={LOCATION_ID}
        locationName={LOCATION_NAME}
        closureData={eodReportData}
        onRefresh={loadData}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          @media print {
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
    </Suspense>
  );
};

export default EndOfDay;